export type NativeMarkingWindow = Window & {
    ReactNativeWebView?: {
        postMessage: (message: string) => void;
    };
    __CC_NATIVE_CAPABILITIES__?: {
        supportsMarkingDataMatrixScanner?: boolean;
    };
};

export type ActiveMarkingCompany = {
    id: string;
    label: string;
};

export function readActiveMarkingCompany(
    storage?: Pick<Storage, 'getItem'>,
): ActiveMarkingCompany {
    const current = storage || (typeof window !== 'undefined' ? window.sessionStorage : undefined);
    if (!current) {
        return {id: '', label: ''};
    }

    try {
        const raw = current.getItem('staffCompanyActive') || current.getItem('companyActive');
        if (!raw) {
            return {id: '', label: ''};
        }

        const company = JSON.parse(raw);
        const id = String(company?.id || company?.companyId || company?.addressId || '');
        const label = String(company?.address || company?.title || company?.name || (id ? 'Салон #' + id : ''));

        return {id, label};
    } catch {
        return {id: '', label: ''};
    }
}

export function isNativeMarkingScannerAvailable(target?: NativeMarkingWindow): boolean {
    const current = target || (typeof window !== 'undefined' ? window as NativeMarkingWindow : undefined);

    return !!(
        current &&
        current.__CC_NATIVE_CAPABILITIES__ &&
        current.__CC_NATIVE_CAPABILITIES__.supportsMarkingDataMatrixScanner === true &&
        current.ReactNativeWebView &&
        typeof current.ReactNativeWebView.postMessage === 'function'
    );
}

export function buildMarkingScanPayload(dataMatrix: string, requestId: string) {
    return {
        // GS/FNC1 separators and percent sequences are significant.
        dataMatrix,
        format: 'datamatrix',
        requestId,
    };
}

export function buildNativeMarkingDeepLink(
    requestId: string,
    sessionToken: string,
    companyId: string,
    returnUrl: string,
): string {
    const url = new URL('chiochio://marking/scan');
    url.searchParams.set('source', 'staff_marking');
    url.searchParams.set('requestId', requestId);
    url.searchParams.set('sessionToken', sessionToken);
    url.searchParams.set('companyId', companyId);
    url.searchParams.set('returnUrl', returnUrl);

    return url.toString();
}

function extractErrorCode(error: any): string {
    const details = error && error.response && error.response.data
        ? error.response.data
        : error && error.details
            ? error.details
            : error;
    const value = details && (details.error || details.code || details.detail);

    return typeof value === 'string' ? value.toLowerCase() : '';
}

export function markingRequestErrorMessage(error: any, operation: 'load' | 'submit'): string {
    const code = extractErrorCode(error);
    const known: Array<[string[], string]> = [
        [['marking_gtin_mismatch', 'gtin_mismatch'], 'Этот DataMatrix относится к другому товару. Проверьте позицию на терминале.'],
        [['marking_code_already_used', 'code_already_used', 'marking_already_used'], 'Этот код маркировки уже был использован. Возьмите другую единицу товара.'],
        [['marking_session_expired', 'session_expired'], 'Время сканирования истекло. Создайте запрос на терминале заново.'],
        [['marking_session_not_pending', 'session_not_pending'], 'Запрос уже обработан или отменён. Обновите список.'],
        [['marking_session_not_found', 'session_not_found'], 'Запрос не найден или уже истёк.'],
        [['company_forbidden', 'company_not_allowed'], 'Нет доступа к салону, который запросил код.'],
        [['company_id_required'], 'Выберите салон перед сканированием DataMatrix.'],
        [['unsupported_format'], 'Нужен именно DataMatrix, а не обычный QR-код.'],
    ];

    for (const item of known) {
        if (item[0].some((candidate) => code.indexOf(candidate) !== -1)) {
            return item[1];
        }
    }

    return operation === 'load'
        ? 'Не удалось загрузить запросы маркировки'
        : 'Не удалось отправить DataMatrix на терминал. Повторите сканирование.';
}
